import { inject } from '@angular/core'
import {
  Router,
  type CanActivateFn,
  type CanMatchFn,
  type Routes,
  type UrlMatcher,
  type UrlSegment,
} from '@angular/router'
import { authGuard } from '../core/auth/auth.guard'
import { AuthService } from '../core/auth/auth.service'
import { BrandService } from '../core/branding/brand.service'
import { brandNameFromSegment } from '../core/branding/branded-address'
import { brandLanguageGuard, neutralLanguageGuard } from '../core/i18n/pre-login-language'
import { ROUTE_TITLES } from '../core/branding/route-titles'
import {
  agencyHostMatch,
  hostBeatsPathGuard,
  notConfirmedPlatformMatch,
} from '../core/branding/host-routing'

/**
 * FR-050 U8 — the branded address `https://<app-host>/a/<name>` (spec §3.5,
 * AC-6.4/6.5/6.9/6.10). R2-B (finding 4): mounted under the `a` segment, so the
 * matchers below see the segments AFTER `/a` — `segments[0]` is the name.
 *
 * A name that fits the syntax (`brandNameFromSegment`) hosts the branded pre-login
 * site. Two routes share the matcher:
 *
 *  1. the name is NOT an active brand (public lookup 404) → the NEUTRAL not-found,
 *     for `/a/<x>` and every `/a/<x>/<anything>` alike (consumes all segments);
 *  2. otherwise → the branded children below. A lookup that failed for any other
 *     reason (network, 5xx, 429) lands here too and renders U6's fail-closed look
 *     (AC-9.5), not the not-found.
 *
 * `/a` alone and `/a/<not the name syntax>` are the neutral not-found without a
 * lookup (the last route). The old top-level `/<name>` has no redirect: it falls to
 * the app's catch-all like any unknown path — a redirect only for active names
 * would let anyone enumerate them (AC-6.5).
 *
 * `/a/Demo-Agency` matches as `demo-agency`: the name is lower-cased, and
 * BrandService skips the lookup for a name it already resolved at bootstrap.
 */

/** The branded-address name of `segments[0]` (the segment after `/a`), or null. */
function nameOf(segments: UrlSegment[]): string | null {
  return segments.length > 0 ? brandNameFromSegment(segments[0].path) : null
}

/** `/a/<name>` + its children (route 2). */
export const brandedAddressMatcher: UrlMatcher = (segments) => {
  if (!nameOf(segments)) return null
  return { consumed: [segments[0]], posParams: { brandName: segments[0] } }
}

/** `/a/<name>/<anything>` in one go (route 1: the neutral not-found has no children). */
export const brandedAddressWholeMatcher: UrlMatcher = (segments) => {
  if (!nameOf(segments)) return null
  return { consumed: segments, posParams: { brandName: segments[0] } }
}

/** Resolve the address, then match only when the name is not an active brand. */
export const brandAddressNotFound: CanMatchFn = async (_route, segments) => {
  const brand = inject(BrandService)
  const router = inject(Router)
  const name = nameOf(segments)
  if (!name) return false
  // FR-051 U7 (AC-4.5): the URL being navigated to, so a live owner's forward keeps
  // the sub-path, query and fragment.
  const nav = router.currentNavigation()
  const target = nav ? router.serializeUrl(nav.extractedUrl) : undefined
  return (await brand.resolveAddress(name, target)) === 'not-found'
}

/**
 * R2-B F1: `/a`, `/a/` and `/a/<not the name syntax>` make no lookup, so put the
 * brand state where an unknown name's 404 puts it — the neutral not-found then has
 * the same tab (no Tivedo title or favicon) as `/a/<unknown name>`.
 */
export const neutralAddressGuard: CanActivateFn = () => {
  inject(BrandService).showNeutralNotFound()
  return true
}

/**
 * The first segments of the signed-in app. Under a branded address (the link base
 * FR-051 builds) they are not pages of their own: signed in → the plain app path,
 * which `/me` brands; signed out → the branded login, then back to the page.
 *
 * Driver decision D9: `p` (the public share pages) is deliberately NOT here —
 * those pages still carry the Tivedo logo until FR-052 AC-1.9 brands them, so
 * `/a/<name>/p` and `/a/<name>/p/**` fall to the branded not-found. Plain `/p/…` is
 * unchanged. Reversible once FR-052 lands.
 */
const APP_FIRST_SEGMENTS = new Set(['portal', 'admin'])

export const brandedAppPathMatcher: UrlMatcher = (segments) =>
  segments.length > 0 && APP_FIRST_SEGMENTS.has(segments[0].path) ? { consumed: segments } : null

export const brandedAppPathGuard: CanActivateFn = async (_route, state) => {
  const router = inject(Router)
  const auth = inject(AuthService)
  const brand = inject(BrandService)
  const tree = router.parseUrl(state.url)
  // Strip the `/a/<name>` segments: the rest is the plain app URL (query + fragment kept).
  const segments = tree.root.children['primary']?.segments ?? []
  const plain = router.createUrlTree(['/', ...segments.slice(2).map((s) => s.path)], {
    queryParams: tree.queryParams,
    fragment: tree.fragment ?? undefined,
  })
  // FIX-COV2 N2: the anonymous-safe session probe first, /me only when signed in —
  // a signed-out hard load makes no failing request (no 401 in the console). Its
  // /me read is `quiet`, so the interceptor never bounces and drops the returnUrl
  // this guard adds.
  const user = await auth.probeSession()
  if (user) return plain
  return router.createUrlTree([brand.preLoginLink('login')], {
    queryParams: { returnUrl: router.serializeUrl(plain) },
  })
}

const loginPage = () => import('../features/login/login.page').then((m) => m.LoginPage)
const brandedNotFound = () =>
  import('../features/not-found/branded-not-found.page').then((m) => m.BrandedNotFoundPage)

/** The pages that exist under an active branded address (spec §3.5). */
export const brandedAddressChildren: Routes = [
  {
    path: '',
    pathMatch: 'full',
    title: ROUTE_TITLES.login,
    canActivate: [brandLanguageGuard],
    loadComponent: loginPage,
  },
  {
    path: 'forgot-password',
    title: ROUTE_TITLES.forgotPassword,
    canActivate: [brandLanguageGuard],
    loadComponent: () =>
      import('../features/forgot-password/forgot-password.page').then((m) => m.ForgotPasswordPage),
  },
  {
    path: 'reset-password',
    title: ROUTE_TITLES.resetPassword,
    canActivate: [brandLanguageGuard],
    loadComponent: () =>
      import('../features/reset-password/reset-password.page').then((m) => m.ResetPasswordPage),
  },
  {
    path: 'accept-invitation',
    title: ROUTE_TITLES.acceptInvitation,
    canActivate: [brandLanguageGuard],
    loadComponent: () =>
      import('../features/accept-invitation/accept-invitation.page').then((m) => m.AcceptInvitationPage),
  },
  {
    path: '403',
    title: ROUTE_TITLES.forbidden,
    // F3 (AC-6.9): in the brand language like every other page under /a/<name>.
    canActivate: [brandLanguageGuard],
    loadComponent: () => import('../features/forbidden/forbidden.page').then((m) => m.ForbiddenPage),
  },
  // F2 (AC-6.9): a login at /a/<name> that still has to pick an account (or has
  // none) stays in the brand on these two post-login pages.
  {
    path: 'select-account',
    title: ROUTE_TITLES.selectAccount,
    canActivate: [authGuard],
    loadComponent: () =>
      import('../features/select-account/select-account.page').then((m) => m.SelectAccountPage),
  },
  {
    path: 'no-access',
    title: ROUTE_TITLES.noAccess,
    canActivate: [authGuard],
    loadComponent: () => import('../features/no-access/no-access.page').then((m) => m.NoAccessPage),
  },
  // AC-6.5 / AC-6.10: no registration under a brand — the branded not-found.
  {
    path: 'register',
    title: ROUTE_TITLES.notFound,
    canActivate: [brandLanguageGuard],
    loadComponent: brandedNotFound,
  },
  { matcher: brandedAppPathMatcher, canActivate: [brandedAppPathGuard], children: [] },
  { path: '**', title: ROUTE_TITLES.notFound, canActivate: [brandLanguageGuard], loadComponent: brandedNotFound },
]

const neutralNotFound = () =>
  import('../features/not-found/neutral-not-found.page').then((m) => m.NeutralNotFoundPage)

/** The children of the top-level `a` route (app.routes.ts), mounted before the catch-all. */
export const brandedAddressRoutes: Routes = [
  // FR-051 U7 (AC-4.4, plan F2): on an agency host the host beats the path — no
  // lookup, the plain path. Inside the `a` route, so `a` keeps one owner
  // (scripts/check-reserved-brand-names.mjs).
  { path: '**', canMatch: [agencyHostMatch], canActivate: [hostBeatsPathGuard], children: [] },
  // FR-051 U7 fix F-A (fail closed): a branded address is looked up ONLY on a host the
  // server confirmed is the platform's. Anything else (a failed host context) gets the
  // neutral "temporarily unavailable" page — never another owner's branded login.
  {
    path: '**',
    canMatch: [notConfirmedPlatformMatch],
    canActivate: [neutralLanguageGuard],
    title: ROUTE_TITLES.unavailable,
    loadComponent: () =>
      import('../features/not-found/host-unavailable.page').then((m) => m.HostUnavailablePage),
  },
  {
    matcher: brandedAddressWholeMatcher,
    canMatch: [brandAddressNotFound],
    canActivate: [neutralLanguageGuard],
    title: ROUTE_TITLES.notFound,
    loadComponent: neutralNotFound,
  },
  { matcher: brandedAddressMatcher, children: brandedAddressChildren },
  // `/a`, `/a/` and `/a/<not the name syntax>[/…]`: nothing to look up — the
  // neutral not-found, the same page (and tab) an unknown name gets.
  {
    path: '**',
    canActivate: [neutralAddressGuard, neutralLanguageGuard],
    title: ROUTE_TITLES.notFound,
    loadComponent: neutralNotFound,
  },
]
